<template>
  <div class="app-container">
    <div>
      <el-select
        v-model="selectedDomain"
        :clearable="true"
        class="search-select"
        type="select"
        style="margin-bottom: 10px"
        :placeholder="$t('common.domain')"
        @input="handleDomainChange($event)"
      >
        <el-option v-for="domain in domains" :key="domain.id" :label="domain.name" :value="domain.id" />
      </el-select>
      <el-select
        v-model="selectedClient"
        :clearable="true"
        class="search-select"
        type="select"
        style="margin-left: 4px;margin-bottom: 10px"
        :placeholder="$t('invoice.clientName')"
        @input="handleClientChange($event)"
      >
        <el-option v-for="client in clients" :key="client.id" :label="client.name" :value="client.id" />
      </el-select>

      <el-select
        v-model="storeID"
        :clearable="true"
        class="search-select"
        type="select"
        style="margin-left: 4px;margin-bottom: 10px"
        :placeholder="$t('invoice.storeCode')"
        @input="refreshList($event)"
      >
        <el-option v-for="store in stores" :key="store.id" :label="store.name" :value="store.id" />
      </el-select>

      <el-input
        v-model="month"
        class="search-select"
        type="number"
        :min="1"
        :max="12"
        :placeholder="$t('invoice.month')"
        style="width: 20%; padding-bottom: 10px"
        @input="refreshList($event)"
      />

      <el-input
        v-model="year"
        class="search-select"
        type="number"
        :min="2020"
        :max="2099"
        :placeholder="$t('invoice.year')"
        style="width: 20%; padding-bottom: 10px"
        @input="refreshList($event)"
      />

      <el-input
        v-model="invoiceNumber"
        class="search-select"
        type="select"
        :placeholder="$t('invoice.invoiceNumber')"
        style="width: 20%; padding-bottom: 10px"
        @input="refreshList($event)"
      />

      <el-select
        v-model="invoiceType"
        :clearable="true"
        class="search-select"
        type="select"
        style="margin-left: 4px;margin-bottom: 10px"
        :placeholder="$t('invoice.invoiceType')"
        @input="refreshList($event)"
      >
        <el-option v-for="type in invoiceTypes" :key="type.code" :label="type.description" :value="type.code" />
      </el-select>

      <div style="float:right;">
        <editor-button
          :disabled="!itemsAreSelected"
          :icon-only="false"
          :loading="creatingDocuments"
          button-translation="invoice.print"
          action="primary"
          @click="createDocuments()"
        />
      </div>
      <el-collapse-transition>
        <el-alert v-if="errorMsg" :title="errorMsg" type="error" :closable="false" />
      </el-collapse-transition>
    </div>
    <div ref="tableContainer">
      <pagination
        :page-sizes="[50, 100]"
        :total="loadedData.total"
        :page.sync="page"
        :limit.sync="pageSize"
        @pagination="refreshList"
      >
      </pagination>
      <el-table
        ref="invoicesList"
        :data="items"
        border
        row-key="externalInvoiceID"
        fit
        :max-height="getMaxTableHeight()"
        @selection-change="handleSelectionChange"
      >
        <el-table-column :label="$t('invoice.invoiceNumber')" prop="invoiceNumber" width="90px">
          <template slot-scope="scope">
            <span>{{ scope.row['invoiceNumber'] }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('invoice.invoiceDate')" prop="invoiceDate" width="110px">
          <template slot-scope="scope">
            <span>{{ formatDate(scope.row['invoiceDate']) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('invoice.clientName')" prop="clientName">
          <template slot-scope="scope">
            <span>{{ scope.row['clientName'] }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('invoice.storeCode')" prop="storeCode">
          <template slot-scope="scope">
            <span>{{ scope.row['storeCode'] }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('invoice.exportClientAt')" prop="exportClientAt" width="130px">
          <template slot-scope="scope">
            <span>{{ formatDate(scope.row['exportClientAt']) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('invoice.exportAccountingAt')" prop="exportAccountingAt" width="130px">
          <template slot-scope="scope">
            <span>{{ formatDate(scope.row['exportAccountingAt']) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('invoice.invoicedInternallyYN')" prop="invoicedInternallyYN" width="80px">
          <template slot-scope="scope">
            <span>{{ formatBool(scope.row['invoicedInternallyYN']) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('invoice.invoiceType')" prop="invoiceType" width="100px">
          <template slot-scope="scope">
            <span>{{ formatInvoiceType(scope.row['invoiceType']) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('invoice.numberOfOrders')" width="80px">
          <template slot-scope="scope">
            <span>{{ scope.row['numberOfOrders'] }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('invoice.totalAmountExcludingVAT')" width="120px" align="right">
          <template slot-scope="scope">
            <span>{{ formatPrice(scope.row['totalAmount']) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('invoice.totalAmountIncludingVAT')" width="120px" align="right">
          <template slot-scope="scope">
            <span>{{ formatPrice(scope.row['totalAmountIncludingVAT']) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.currency')" width="90px" align="center">
          <template slot-scope="scope">
            <span>{{ scope.row['currencyISOCode'] }}</span>
          </template>
        </el-table-column>
        <el-table-column
          fixed="right"
          type="selection"
          :reserve-selection="true"
          width="40"
          align="center"
          :selectable="() => true"
        >
        </el-table-column>
      </el-table>
      <el-dialog
        :title="dialogTitle"
        :visible.sync="dialogVisible"
        :close-on-click-modal="false"
        :append-to-body="true"
        :width="'90%'"
        @closed="handleDialogClosed"
      >
        <document-viewer-dialog :pdfs="createdDocuments"> </document-viewer-dialog>
      </el-dialog>
    </div>
  </div>
</template>

<script>
import EditorButton from '@/components/crud/EditorButton';
import Pagination from '@/components/Pagination';
import { toDateTimeString } from '@/utils/date';
import DocumentViewerDialog from '@/pages/invoicing/DocumentViewerDialog';

export default {
  name: 'Planning',
  components: {
    EditorButton,
    Pagination,
    DocumentViewerDialog
  },
  data() {
    return {
      loading: false,
      loadedData: { items: [], total: 0 },
      errorMsg: '',
      domainID: null,
      clientID: null,
      storeID: null,
      month: null,
      year: null,
      invoiceNumber: null,
      invoiceType: null,
      page: 1,
      pageSize: 50,
      multipleSelection: [],
      creatingDocuments: false,
      dialogTitle: '',
      dialogVisible: false,
      createdDocuments: []
    };
  },
  computed: {
    listParams() {
      return {
        domainID: this.selectedDomain,
        clientID: this.clientID,
        storeID: this.storeID,
        month: this.month,
        year: this.year,
        invoiceNumber: this.invoiceNumber,
        invoiceType: this.invoiceType,
        page: this.page,
        pageSize: this.pageSize
      };
    },
    items() {
      return this.loadedData.items;
    },
    globalSelectedDomain() {
      return this.$store.getters['domain/getDefaultSelectValueDomainID'];
    },
    selectedDomain: {
      get() {
        return this.domainID || this.globalSelectedDomain;
      },
      set(newDomain) {
        return (this.domainID = newDomain);
      }
    },
    selectedClient: {
      get() {
        return this.clientID;
      },
      set(newClient) {
        return (this.clientID = newClient);
      }
    },
    dateTimeFormat() {
      return this.$store.getters.dateTimeFormat;
    },
    domains() {
      return this.$store.getters.domains;
    },
    clients() {
      return this.$store.getters['client/loadedItems'].filter(e => e.domainID === this.selectedDomain);
    },
    stores() {
      return this.$store.getters['store/loadedItems'].filter(
        e => !this.selectedClient || e.clientID === this.selectedClient
      );
    },
    invoiceTypes() {
      return this.$store.getters['option/loadedOptions']('invoiceType');
    },
    itemsAreSelected() {
      return +this.multipleSelection.length;
    }
  },
  watch: {
    globalSelectedDomain(newVal) {
      this.clientID = null;
      this.selectedClient = null;
      this.refreshList();
      this.clearSelection();
    }
  },
  created() {
    this.$store.dispatch('client/getItems');
    this.$store.dispatch('store/getItems');
    this.refreshList();
  },
  methods: {
    handleSelectionChange(val) {
      this.multipleSelection = val;
    },
    handleDialogClosed() {
      this.createdDocuments = [];
    },
    createDocuments() {
      this.creatingDocuments = true;
      this.$store
        .dispatch('externalInvoice/createDocuments', {
          invoices: this.multipleSelection.map(i => i.externalInvoiceID)
        })
        .then(data => {
          this.createdDocuments = data;
          this.dialogVisible = true;
        })
        .finally(() => {
          this.creatingDocuments = false;
          this.refreshList();
          this.clearSelection();
        });
    },
    refreshList() {
      this.loading = true;
      this.$store
        .dispatch('externalInvoice/getInvoices', this.listParams)
        .then(data => {
          this.loadedData.items = data.items;
          this.loadedData.total = data.total;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    handleDomainChange() {
      this.clientID = null;
      this.selectedClient = null;
      this.storeID = null;
      this.refreshList();
      this.clearSelection();
    },
    handleClientChange() {
      this.storeID = null;
      this.refreshList();
      this.clearSelection();
    },
    formatPrice(cellValue) {
      if (isNaN(cellValue)) {
        return '';
      }
      return `${cellValue.toFixed(2)}`;
    },
    getMaxTableHeight() {
      if (this.$refs.tableContainer) {
        return window.innerHeight - this.$refs.tableContainer.getBoundingClientRect().top - 40;
      }
    },
    clearSelection() {
      this.multipleSelection = [];
      this.$refs.invoicesList.clearSelection();
    },
    formatDate(cellValue) {
      return cellValue && toDateTimeString(cellValue, 'DD-MM-YYYY');
    },
    formatBool(cellValue) {
      return cellValue ? this.$t('common.yes') : this.$t('common.no');
    },
    formatInvoiceType(cellValue) {
      const option =
        cellValue !== null &&
        this.$store.getters['option/loadedOptions']('invoiceType').find(item => item.code === cellValue);
      return option && option.description;
    }
  }
};
</script>
